import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { catalogApi, type ClientDetail, type ClientInput } from '@/api/catalogApi'
import { Field } from '@/components/common/Field'
import { errorMessage } from '@/components/common/States'
import { Page } from '@/components/layout/Page'
import { PageHeading } from '@/components/layout/PageHeading'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { BackLink, ListState, StatusPill, SuperAdminGate, TabBar } from '@/components/admin/catalog/CatalogShared'
import { ClientForm } from '@/components/admin/catalog/ClientForm'
import { EnvironmentsPanel } from '@/components/admin/catalog/EnvironmentsPanel'
import { TenantsPanel } from '@/components/admin/catalog/TenantsPanel'
import { useCatalogClient, useCatalogMutation } from '@/hooks/useCatalog'
import { useToast } from '@/providers/ToastProvider'

const TABS = ['details', 'tenants', 'environments'] as const
type Tab = (typeof TABS)[number]

/** /admin/clients/:id — Details | Tenants | Environments. */
export default function CatalogClientPage() {
  return (
    <SuperAdminGate>
      <ClientPage />
    </SuperAdminGate>
  )
}

function ClientPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const [params, setParams] = useSearchParams()
  const client = useCatalogClient(id)
  const tab: Tab = TABS.find((x) => x === params.get('tab')) ?? 'details'
  const c = client.data

  return (
    <Page>
      <BackLink to="/admin/clients" />
      <PageHeading
        eyebrow={t('catalog.eyebrow')}
        title={c?.name ?? t('catalog.clients.title')}
        actions={
          c ? (
            <div className="flex items-center gap-3">
              <span className="font-mono text-meta text-ink-muted">{c.slug}</span>
              <StatusPill status={c.status} />
            </div>
          ) : null
        }
      />
      <ListState query={client}>
        {c ? (
          <>
            <TabBar tabs={TABS} value={tab} labelKey="catalog.clients.tabs" onChange={(x) => setParams(x === 'details' ? {} : { tab: x }, { replace: true })} />
            {tab === 'details' ? <DetailsTab client={c} /> : tab === 'tenants' ? <TenantsPanel client={c} /> : <EnvironmentsPanel client={c} />}
          </>
        ) : null}
      </ListState>
    </Page>
  )
}

function DetailsTab({ client }: { client: ClientDetail }) {
  const { t } = useTranslation()
  const { notify } = useToast()
  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState('')
  const update = useCatalogMutation((input: ClientInput) => catalogApi.updateClient(client.id, input))
  const suspended = client.status !== 'active'
  const status = useCatalogMutation(() => catalogApi.setClientStatus(client.id, suspended ? 'reactivate' : 'suspend', reason.trim() || undefined))

  return (
    <div className="flex flex-col gap-8">
      <ClientForm
        key={`${client.id}:${client.updatedAt}`}
        client={client}
        pending={update.isPending}
        error={update.error}
        onSubmit={(input) => update.mutate(input, { onSuccess: () => notify({ title: t('catalog.products.saved') }) })}
      />
      <section className="flex max-w-2xl items-center justify-between gap-4 border-t border-line pt-6">
        <div className="flex flex-col gap-1">
          <p className="text-body font-medium text-ink-strong">{suspended ? t('catalog.clients.reactivate') : t('catalog.clients.suspend')}</p>
          <p className="text-meta text-ink-muted">{suspended ? t('catalog.clients.suspendedNote') : t('catalog.clients.suspendBody')}</p>
        </div>
        <Button variant={suspended ? 'default' : 'destructive'} onClick={() => setConfirming(true)}>
          {suspended ? t('catalog.clients.reactivate') : t('catalog.clients.suspend')}
        </Button>
      </section>

      <Dialog
        open={confirming}
        onOpenChange={(o) => {
          if (!o) {
            setConfirming(false)
            setReason('')
            status.reset()
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t(suspended ? 'catalog.clients.reactivateTitle' : 'catalog.clients.suspendTitle', { name: client.name })}</DialogTitle>
            <DialogDescription>{t(suspended ? 'catalog.clients.reactivateBody' : 'catalog.clients.suspendBody')}</DialogDescription>
          </DialogHeader>
          <Field id="cl-reason" label={t('catalog.clients.reason')}>
            <Textarea id="cl-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          {status.error ? (
            <p role="alert" className="rounded-md bg-status-escalated-bg px-3 py-2 text-meta text-status-escalated-fg">
              {errorMessage(status.error)}
            </p>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={status.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={suspended ? 'default' : 'destructive'}
              disabled={status.isPending}
              onClick={() =>
                status.mutate(undefined, {
                  onSuccess: () => {
                    setConfirming(false)
                    setReason('')
                  },
                })
              }
            >
              {suspended ? t('catalog.clients.reactivate') : t('catalog.clients.suspend')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

